import React, { createContext, useCallback, useContext, useMemo, useEffect } from 'react';
import { createRequestBody, doRas } from '@upgrade/ras/tracker';
import { merge } from 'lodash';
import { useLingui } from '@lingui/react/macro';
import { joinPaths } from '@upgrade/ui-utils/utils/url';
import moment from 'moment';
import CAFlag from '../../static/img/flags/ca-flag.png';
import USFlag from '../../static/img/flags/us-flag.png';
import { FLEX_PAY } from '../constants/path-constants';
import { useOptimizely } from './optimizely';
import { RAS_ENDPOINT } from './ras';
import { activateLocale } from '../i18n';

export const LOCALES = {
  EN_US: {
    code: 'en-US',
    label: 'USA / English',
    flag: USFlag,
    country: 'United States',
    demonym: 'US',
    homePage: '/',
    pathname: '',
    language: 'en'
  },
  FR_CA: {
    code: 'fr-CA',
    label: 'Canada / Français',
    flag: CAFlag,
    country: 'Canada',
    demonym: 'Canadian',
    homePage: joinPaths('/fr-ca', FLEX_PAY),
    pathname: '/fr-ca',
    language: 'fr'
  },
  EN_CA: {
    code: 'en-CA',
    label: 'Canada / English',
    flag: CAFlag,
    country: 'Canada',
    demonym: 'Canadian',
    homePage: joinPaths('/en-ca', FLEX_PAY),
    pathname: '/en-ca',
    language: 'en'
  }
};

const LocaleContext = createContext({
  locale: LOCALES.EN_US
});

export const useLocale = () => {
  return useContext(LocaleContext);
};

export const LocaleProvider = ({ children, location }) => {
  const { i18n } = useLingui();
  const locale = useMemo(() => {
    const currentLocale = Object.values(LOCALES).find(loc => loc.code === i18n.locale) || LOCALES.EN_US;
    return {
      ...currentLocale
    };
  }, [i18n.locale]);

  const setLocale = useCallback(newLocale => {
    if (Object.values(LOCALES).includes(newLocale)) {
      activateLocale(newLocale.code);
      moment.locale(newLocale.code);
    }
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const pathname = window?.location?.pathname || '';
      const locales = Object.values(LOCALES);
      const matchedLocale = locales.find(loc => loc.pathname && pathname.startsWith(loc.pathname));
      setLocale(matchedLocale || LOCALES.EN_US);
    }
  }, [setLocale]);

  const trackWithLocale = useMemo(() => {
    const formattedLocaleCode = locale.code.replace('-', '_');
    return ({ event }) => {
      doRas(RAS_ENDPOINT, 'click', '', {}, () =>
        merge({}, createRequestBody(), {
          event: `${formattedLocaleCode}_${event}`
        })
      );
    };
  }, [locale]);

  const isCanadaEnglish = locale.code === LOCALES.EN_CA.code;
  const isCanadaFrench = locale.code === LOCALES.FR_CA.code;
  const isCanada = isCanadaFrench || isCanadaEnglish;
  const localePath = `/${locale.code}`;

  const contextValue = useMemo(
    () => ({ locale, setLocale, isCanada, isCanadaFrench, isCanadaEnglish, location, localePath, trackWithLocale }),
    [locale, isCanada, isCanadaFrench, isCanadaEnglish, setLocale, location, localePath, trackWithLocale]
  );

  return <LocaleContext.Provider value={contextValue}>{children}</LocaleContext.Provider>;
};

const LOCALE_SWITCHER_EXPERIMENT = 'language_switcher';

const LOCALE_SWITCHER_EXPERIMENT_VARIATIONS = {
  ENABLED: 'on',
  DISABLED: 'off'
};

export const useLocaleSwitcherExperiment = () => {
  const { activateExperiment, isReady, getExperiment } = useOptimizely();
  const localeSwitcherExperiment = getExperiment(LOCALE_SWITCHER_EXPERIMENT);

  useEffect(() => {
    if (activateExperiment) {
      activateExperiment(LOCALE_SWITCHER_EXPERIMENT);
    }
  }, [activateExperiment]);

  return {
    isExperimentEnabled: isReady && localeSwitcherExperiment === LOCALE_SWITCHER_EXPERIMENT_VARIATIONS.ENABLED
  };
};

// A list of pages that are shared across ALL locales
const SHARED_PAGES = {
  '/flex-pay/': true,
  '/flex-pay/find-partners/': true,
  '/flex-pay/partners/': true,
  '/flex-pay/partners/travel/': true,
  '/flex-pay/partners/retail/': true,
  '/flex-pay/partners/hospitality/': true,
  '/flex-pay/partners/become-a-travel-agent-partner/': true,
  '/flex-pay/frequently-asked-questions/': true,
  '/flex-pay/lenders/': true,
  '/flex-pay/reviews/': true,
  '/about/': true
};

const removeLocaleFromPath = path => {
  if (!path) return path;

  const localeCodes = Object.values(LOCALES)
    .map(locale => locale.code)
    .filter(Boolean);

  const regex = new RegExp(`^/(${localeCodes.join('|')})(/|$)`, 'i');

  return path.replace(regex, '/');
};

export const getPathnameForLocale = ({ url, locale }) => {
  if (!url) return locale.homePage;

  const cleanUrl = removeLocaleFromPath(url);

  if (SHARED_PAGES[cleanUrl]) {
    return joinPaths(locale.pathname || '', cleanUrl);
  }

  return locale.homePage;
};
